import { Pagination } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import useIsMobile from '../../utils/useIsMobile';

const Paginate = ({
    pages,
    page,
    isAdmin = false,
    keyword = '',
    categoryId = '',
    brandId = '',
    ruc = '',
    sort = '',
    category = '', // Added category filter support
    isCompany = false,
    promotionId = '',
    isUserOrders = false, // Added user orders support
    isDiscounts = false,
    scrollTargetId = '', // NEW: optional scroll target
}) => {
    const isMobile = useIsMobile();        // < 768px
    const isTinyPhone = useIsMobile(480);  // < 480px (iPhone SE, small Androids)

    // 3-tier page count: keeps single-row readability across breakpoints.
    // Tiny phones get 3 (not 2) so that page N always shows N-1, N, N+1 —
    // a window of 2 only rendered "1 2 … last" from page 2 onward.
    const maxPagesToShow = isTinyPhone ? 3 : isMobile ? 5 : 10;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Rebalance the range near boundaries so we keep a stable amount of pages visible.
    let startPage = Math.max(1, page - halfRange);
    let endPage = Math.min(pages, startPage + maxPagesToShow - 1);
    startPage = Math.max(1, endPage - maxPagesToShow + 1);

    const handlePrev = () => (startPage > 1 ? startPage - 1 : 1);
    const handleNext = () => (endPage < pages ? endPage + 1 : pages);

    const createLink = (pageNum) => {
        if (isUserOrders) {
            return pageNum === 1
                ? '/orders'
                : `/orders/page/${pageNum}`;
        }
        if (isDiscounts) {
            return pageNum === 1
                ? '/discounts'
                : `/discounts/page/${pageNum}`;
        }
        if (isAdmin === 'users') {
            return keyword
                ? `/admin/userlist/search/${keyword}/page/${pageNum}`
                : `/admin/userlist/page/${pageNum}`;
        }
        if (isAdmin === 'orders') {
            return keyword
                ? `/admin/orderlist/search/${keyword}/page/${pageNum}`
                : `/admin/orderlist/page/${pageNum}`;
        }
        if (isAdmin === 'outofstock') {
            return keyword
                ? `/admin/outofstock/search/${keyword}/page/${pageNum}`
                : `/admin/outofstock/page/${pageNum}`;
        }
        if (isAdmin) {
            return keyword
                ? `/admin/productlist/search/${keyword}/page/${pageNum}`
                : `/admin/productlist/page/${pageNum}`;
        } else if (promotionId) {
            return pageNum === 1
                ? `/promotion/${promotionId}`
                : `/promotion/${promotionId}/page/${pageNum}`;
        } else if (keyword) {
            return pageNum === 1 ? `/search/${keyword}` : `/search/${keyword}/page/${pageNum}`;
        } else if (categoryId) {
            return pageNum === 1
                ? `/category/${categoryId}`
                : `/category/${categoryId}/page/${pageNum}`;
        } else if (brandId) {
            return pageNum === 1 ? `/brand/${brandId}` : `/brand/${brandId}/page/${pageNum}`;
        } else if (ruc) {
            return pageNum === 1 ? `/company/${ruc}` : `/company/${ruc}/page/${pageNum}`;
        } else {
            return pageNum === 1 ? '/' : `/page/${pageNum}`;
        }
    };

    const getSearchParams = () => {
        const params = new URLSearchParams();
        if (sort) params.set('sort', sort);
        if (isCompany && category) params.set('category', category);
        return params.toString() ? `?${params.toString()}` : '';
    };

    // Scroll to the target element if provided
    const handleScroll = () => {
        if (scrollTargetId) {
            const el = document.getElementById(scrollTargetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    return (
        pages > 1 && (
            <div className="d-flex justify-content-center mt-4 mb-4 custom-pagination-wrap">
                <Pagination className="custom-pagination">
                    {/* Previous button */}
                    {startPage > 1 && (
                        <LinkContainer
                            to={{
                                pathname: createLink(handlePrev()),
                                search: getSearchParams(),
                            }}
                        >
                            <Pagination.Prev onClick={handleScroll} />
                        </LinkContainer>
                    )}

                    {/* First page */}
                    {startPage > 1 && (
                        <LinkContainer
                            to={{
                                pathname: createLink(1),
                                search: getSearchParams(),
                            }}
                        >
                            <Pagination.Item onClick={handleScroll}>1</Pagination.Item>
                        </LinkContainer>
                    )}

                    {/* Ellipsis before range */}
                    {startPage > 2 && <Pagination.Ellipsis disabled />}

                    {/* Page range */}
                    {[...Array(endPage - startPage + 1)].map((_, index) => {
                        const pageNum = startPage + index;
                        return (
                            <LinkContainer
                                key={pageNum}
                                to={{
                                    pathname: createLink(pageNum),
                                    search: getSearchParams(),
                                }}
                            >
                                <Pagination.Item active={pageNum === page} onClick={handleScroll}>
                                    {pageNum}
                                </Pagination.Item>
                            </LinkContainer>
                        );
                    })}

                    {/* Ellipsis after range */}
                    {endPage < pages - 1 && <Pagination.Ellipsis disabled />}

                    {/* Last page */}
                    {endPage < pages && (
                        <LinkContainer
                            to={{
                                pathname: createLink(pages),
                                search: getSearchParams(),
                            }}
                        >
                            <Pagination.Item onClick={handleScroll}>{pages}</Pagination.Item>
                        </LinkContainer>
                    )}

                    {/* Next button */}
                    {endPage < pages && (
                        <LinkContainer
                            to={{
                                pathname: createLink(handleNext()),
                                search: getSearchParams(),
                            }}
                        >
                            <Pagination.Next onClick={handleScroll} />
                        </LinkContainer>
                    )}
                </Pagination>
            </div>
        )
    );
};

export default Paginate;
